import { computed, type Ref } from 'vue';

export interface UrlDataForPrefix {
  prefixCode?: string;
  numbering: number;
  sequential: number;
}

export function buildUrlPrefix(
  data: UrlDataForPrefix | undefined
): string | null {
  if (!data?.prefixCode) return null;
  const numberingStr = String(data.numbering).padStart(2, '0');
  return `${data.prefixCode}${numberingStr}.${data.sequential}`;
}

export function useUrlBatchDuplicatePrefixes(
  urlDataMap: Ref<Map<number, UrlDataForPrefix & { url?: string; changelog?: string; managerId?: number | string }>>,
  urlUploadInstances: Ref<Array<{ id: number }>>
) {
  const urlBatchDuplicatePrefixes = computed(() => {
    const countByPrefix = new Map<string, number>();
    for (const instance of urlUploadInstances.value) {
      const data = urlDataMap.value.get(instance.id);
      const prefix = buildUrlPrefix(data);
      if (prefix) {
        countByPrefix.set(prefix, (countByPrefix.get(prefix) ?? 0) + 1);
      }
    }
    return [...countByPrefix.entries()]
      .filter(([, count]) => count > 1)
      .map(([p]) => p);
  });

  const urlBatchDuplicatePrefixesSet = computed(
    () => new Set(urlBatchDuplicatePrefixes.value)
  );

  const isInstancePrefixDuplicate = (instanceId: number): boolean => {
    const data = urlDataMap.value.get(instanceId);
    const prefix = buildUrlPrefix(data);
    return prefix !== null && urlBatchDuplicatePrefixesSet.value.has(prefix);
  };

  return {
    urlBatchDuplicatePrefixes,
    urlBatchDuplicatePrefixesSet,
    isInstancePrefixDuplicate,
  };
}
